/**
 * Global singleton that manages history API patching for all widget instances.
 * Ensures pushState/replaceState are only patched once, regardless of instance count.
 *
 * This solves the problem of multiple widget instances each trying to patch the
 * history API, which would create nested function wrappers and cause issues.
 */
class HistoryPatcher {
  private static instance: HistoryPatcher | null = null;
  private readonly listeners: Map<string, () => void> = new Map();
  private originalPushState: History['pushState'] | null = null;
  private originalReplaceState: History['replaceState'] | null = null;
  private isPatched: boolean = false;

  private constructor() {}

  static getInstance(): HistoryPatcher {
    if (!HistoryPatcher.instance) {
      HistoryPatcher.instance = new HistoryPatcher();
    }
    return HistoryPatcher.instance;
  }

  /**
   * Register an instance's navigation listener.
   * First registration patches the history API.
   */
  register(instanceId: string, listener: () => void): void {
    this.listeners.set(instanceId, listener);

    if (!this.isPatched) {
      this.patch();
    }
  }

  /**
   * Unregister an instance's listener.
   * Last unregistration restores the original history API.
   */
  unregister(instanceId: string): void {
    this.listeners.delete(instanceId);

    if (this.listeners.size === 0 && this.isPatched) {
      this.unpatch();
    }
  }

  private patch(): void {
    if (this.isPatched || typeof window === 'undefined') return;

    const history = window.history;
    this.originalPushState = history.pushState.bind(history);
    this.originalReplaceState = history.replaceState.bind(history);

    /* eslint-disable @typescript-eslint/no-explicit-any */
    history.pushState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      const ret = this.originalPushState!(data as any, unused, url as any);
      this.notifyAllListeners();
      return ret;
    }) as any;

    history.replaceState = ((
      data: unknown,
      unused: string,
      url?: string | URL | null
    ) => {
      const ret = this.originalReplaceState!(data as any, unused, url as any);
      this.notifyAllListeners();
      return ret;
    }) as any;
    /* eslint-enable @typescript-eslint/no-explicit-any */

    this.isPatched = true;
  }

  private unpatch(): void {
    if (
      !this.isPatched ||
      !this.originalPushState ||
      !this.originalReplaceState
    ) {
      return;
    }

    window.history.pushState = this.originalPushState;
    window.history.replaceState = this.originalReplaceState;

    this.originalPushState = null;
    this.originalReplaceState = null;
    this.isPatched = false;
  }

  private notifyAllListeners(): void {
    this.listeners.forEach(listener => {
      try {
        listener();
      } catch (error) {
        console.error('[HistoryPatcher] Listener error:', error);
      }
    });
  }

  /**
   * Check if the history API is currently patched.
   * Useful for debugging.
   */
  isPatchedState(): boolean {
    return this.isPatched;
  }

  /**
   * Get the number of registered listeners.
   * Useful for debugging.
   */
  getListenerCount(): number {
    return this.listeners.size;
  }
}

export const getHistoryPatcher = (): HistoryPatcher =>
  HistoryPatcher.getInstance();
